import React, { useState, useContext } from "react";
import { Star, StarBorder, IosShare, Close } from "@mui/icons-material";
import { borderRadius, fontSize, fontWeight } from "@mui/system";
import { Sheet } from "react-modal-sheet";
import { input } from "@testing-library/user-event/dist/cjs/event/input.js";
import { transform } from "motion";
import ShopButton from "./ShopButton";
import { collection, addDoc } from "firebase/firestore";
import { EnvContext } from "../context/EnvContext";
import config from "../config";
import { useFirestore } from "../firebase/FirestoreContext";

// Single Review Component
export const ReviewsElement = ({ rating, title, user_name, review, verification, date, reviewId, productId }) => {
  const db = useFirestore();
  const environment = useContext(EnvContext);
  const [isExpanded, setIsExpanded] = useState(false);
  const [isOpen, setOpen] = useState(false);
  const [reportSent, setReportSent] = useState(false);
  const [reportReason, setReportReason] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false); // Track submission state

  const collectionPath = environment === "staging" ? config.environment.staging.collectionPath : config.environment.production.collectionPath;

  const toggleReview = () => {
    setIsExpanded((prevState) => !prevState); // Toggle between expanded/collapsed
  };

  const handleReasonChange = (reason) => {
    setReportReason(reason);
  };

  const handleReportSubmit = async () => {
    if (!reportReason) {
      alert("Please select a reason for reporting.");
      return;
    }

    setIsSubmitting(true); // Start submitting

    try {
      const reportData = {
        reviewID: reviewId,
        productID: productId,
        reason: reportReason,
        timestamp: new Date(),
      };

      if (!db) {
        throw new Error("Firestore database instance is not available.");
      }

      const reportCollection = collection(db, `${collectionPath}Products/ProductsList/DataBase/${productId}/Reviews/${reviewId}/Reports`);
      await addDoc(reportCollection, reportData);

      setReportSent(true);
      setTimeout(() => {
        setOpen(false);
        setReportSent(false);
        setReportReason(null);
        setIsSubmitting(false); // Done submitting
      }, 2000);
    } catch (error) {
      console.error("Error submitting report:", error);
      alert("Failed to submit report. Please try again.");
      setIsSubmitting(false); // Also done submitting in case of error
    }
  };

  return (
    <div style={styles.container}>
      {/* BottomSheet */}
      <Sheet isOpen={isOpen} onClose={() => setOpen(false)} snapPoints={[0.9]} disableDrag={true}>
        <Sheet.Container style={{ overflowY: "auto" }}>
          <Sheet.Header>
            <button
              onClick={() => {
                setOpen(false);
              }}
              style={{ ...styles?.report?.closeButton, zIndex: 100 }}
            >
              <Close />
            </button>
          </Sheet.Header>
          <Sheet.Content style={{ ...styles?.report?.container }}>
            {reportSent ? (
              <div style={{ textAlign: "center" }}>
                <h3 style={{ ...styles.report.title, paddingBottom: 10 }}>Thank you!</h3>
                <p style={{ fontSize: ".8rem", paddingBottom: 10 }}>
                  We’ll check if this review meets our{" "}
                  <a href="#" style={{ textDecoration: "none" }}>
                    community guidelines
                  </a>
                  . If it doesn’t, we’ll remove it.
                </p>
                <img src="/images/report.png" alt="report" style={{ width: "100%", height: "auto", borderRadius: 8, marginTop: 8 }} />
              </div>
            ) : (
              <>
                <h3 style={styles.report.title}>Report this review</h3>
                <p style={{ paddingBottom: 10 }}>Optional: Why are you reporting this?</p>
                <form style={styles.form}>
                  {["Off topic", "Spam", "Inappropriate", "Fake", "Other"].map((label, idx) => (
                    <div style={styles.report.inputArea} key={idx}>
                      <input type="checkbox" style={styles.report.input} checked={reportReason === label} onChange={() => handleReasonChange(label)} />
                      <div style={styles.report.inputName}>
                        <div>{label}</div>
                        <div style={{ opacity: 0.6 }}>
                          {
                            {
                              "Off topic": "Not about the product",
                              Spam: "Promotion of other products",
                              Inappropriate: "Disrespectful, hateful, obscene",
                              Fake: "Paid for, inauthentic",
                              Other: "Something else",
                            }[label]
                          }
                        </div>
                      </div>
                    </div>
                  ))}
                  <p style={{ fontSize: ".8rem", paddingBottom: 10 }}>
                    We’ll check if this review meets our{" "}
                    <a href="#" style={{ textDecoration: "none" }}>
                      community guidelines
                    </a>
                    . If it doesn’t, we’ll remove it.
                  </p>
                  {isSubmitting ? (
                    <div>Submitting...</div>
                  ) : (
                    <ShopButton
                      text={"Submit"}
                      theme={"dark"}
                      containerStyles={{ width: "100%" }}
                      buttonStyle={{ flex: 1 }}
                      onClick={() => handleReportSubmit()}
                    />
                  )}
                </form>
              </>
            )}
          </Sheet.Content>
        </Sheet.Container>
        <Sheet.Backdrop />
      </Sheet>
      {/* Review */}

      <div style={styles.user_name}>{user_name}</div>
      <Rating value={rating} readOnly iconStyle={{ fontSize: "1rem" }} />
      <div style={styles.title}>{title}</div>
      <div style={styles.verification}>{verification}</div>
      <div style={styles.content}>
        <div style={styles.review}>
          {
            // if review is more than 200 characters, show only 200 characters and "<span>See more</span>"
            review?.length > 200 ? (
              <>
                {isExpanded ? review : review.slice(0, 200) + "... "}
                <span style={{ color: " #3B72FE", cursor: "pointer", paddingLeft: 5 }} onClick={toggleReview}>
                  {isExpanded ? "Show less" : "See more"}
                </span>
              </>
            ) : (
              review // If the review is short, just display it
            )
          }
        </div>
      </div>
      <div style={styles?.footer}>
        <div style={styles.reportButton} onClick={() => setOpen(true)}>
          Report
        </div>
      </div>
    </div>
  );
};

// Rating Component (Star Rating)
export const Rating = ({ value, style, iconStyle, readOnly }) => {
  return (
    <div style={{ ...styles.rating.container, ...style, ...iconStyle }}>
      {Array.from({ length: 5 }).map((_, index) => (
        <div style={styles.rating.star} key={index}>
          {index < value ? <Star style={{ ...iconStyle }} /> : <StarBorder style={{ ...iconStyle }} />}
        </div>
      ))}
    </div>
  );
};

// Styles for the components
const styles = {
  container: {
    display: "flex",
    flexDirection: "column",
    margin: "8px 0",
    marginBottom: 30,
  },
  //
  name: {
    fontSize: "0.9rem",
    fontWeight: 300,
    paddingBottom: 4,
  },
  title: {
    fontSize: "1rem",
    fontWeight: "600",
    paddingBottom: 5,
  },
  verification: {
    fontSize: 14,
    color: "gray",
    marginBottom: 0,
  },
  content: {
    marginTop: 8,
  },
  review: {
    fontSize: 14,
    lineHeight: 1.5,
  },
  footer: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginTop: 12,
  },
  reportButton: {
    padding: "6px 16px",
    border: "1px solid black",
    borderRadius: 50,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    marginRight: 8,
    cursor: "pointer"
  },

  user_name: {
    fontSize: ".9rem",
    fontWeight: 400,
    paddingBottom: 5,
  },

  //
  rating: {
    container: {
      display: "flex",
      flexDirection: "row",
    },
    star: {
      icon: {
        fontSize: 24,
      },
    },
  },

  // BottomSheet Styles
  report: {
    container: {
      padding: "40px 20px 20px 20px",
      fontSize: ".9rem",
    },
    title: {
      fontSize: "1rem",
      fontWeight: 600,
      paddingBottom: 3,
    },
    inputArea: {
      display: "flex",
      alignItems: "center",
      paddingBottom: 10,
    },
    input: {
      transform: "scale(.8)",
      marginTop: 3,
    },
    inputName: {
      display: "flex",
      flexDirection: "column",
      paddingLeft: 10,
    },
    closeButton: {
      position: "absolute",
      top: "10px",
      right: "10px",
      backgroundColor: "transparent",
      border: "none",
      fontSize: "20px",
      cursor: "pointer",
      color: "black",
    },
  },
};

export default ReviewsElement;
